import React, { useEffect, useMemo, useState } from 'react';
import './Gallery.css';
import { apiGet } from '../api';

const Gallery = ({ language }) => {
  const [remoteImages, setRemoteImages] = useState([]); // ungrouped singles
  const [sets, setSets] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalItems, setModalItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const setImageIds = useMemo(() => new Set((sets || []).flatMap(g => (g.images || []).map(im => im.id)).filter(Boolean)), [sets]);
  
  useEffect(() => {
    let ignore = false;
    (async () => {
      let validSets = [];
      try {
        const groups = await apiGet('/api/gallery-sets', { locale: language });
        validSets = Array.isArray(groups) ? groups.filter((g) => Array.isArray(g.images) && g.images.length > 0) : [];
        if (!ignore) setSets(validSets);
      } catch (e) {
        console.warn('gallery-sets fetch failed', e);
      }
      try {
        const data = await apiGet('/api/gallery-images', { locale: language, sort: 'order:asc' });
        if (!ignore && Array.isArray(data)) {
          const idsInSets = new Set((validSets || []).flatMap(g => (g.images || []).map(im => im.id)).filter(Boolean));
          // Exclude any item that is part of a set (by ID), even if groupId is missing
          const singles = data.filter((it) => !idsInSets.has(it.id) && !it.groupId);
          const mapped = singles.map((it) => {
            const type = it.type || (it.youtubeUrl ? 'youtube' : (it.videoUrl ? 'video' : 'image'));
            const cap = it.caption || '';
            const desc = it.description || '';
            return {
              src: it.imageUrl || null,
              videoUrl: it.videoUrl || null,
              youtubeUrl: it.youtubeUrl || null,
              type,
              caption: { [language]: cap },
              description: { [language]: desc },
            };
          });
          setRemoteImages(mapped.filter((m) => m.src || m.videoUrl || m.youtubeUrl));
        }
      } catch (e) {
        console.warn('gallery-images fetch failed', e);
      }
    })();
    return () => { ignore = true; };
  }, [language]);
  const imagesToShow = remoteImages;
  
  const getYouTubeThumbnail = (url) => {
    const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/)?.[1];
    return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
  };
  
  const getYouTubeEmbedUrl = (url) => {
    const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/)?.[1];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };
  
  const openModal = (items, index) => {
    setModalItems(items || []);
    setCurrentIndex(Math.max(0, Math.min(index || 0, (items || []).length - 1)));
    setSelectedItem((items || [])[index || 0] || null);
  };
  
  const closeModal = () => {
    setSelectedItem(null);
    setModalItems([]);
    setCurrentIndex(0);
  };

  const showAt = (idx) => {
    if (!modalItems.length) return;
    const n = (idx + modalItems.length) % modalItems.length;
    setCurrentIndex(n);
    setSelectedItem(modalItems[n]);
  };
  const nextItem = () => showAt(currentIndex + 1);
  const prevItem = () => showAt(currentIndex - 1);

  useEffect(() => {
    if (!selectedItem) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight') nextItem();
      if (e.key === 'ArrowLeft') prevItem();
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, currentIndex, modalItems.length]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    const original = document.body.style.overflow;
    if (selectedItem) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = original || '';
    };
  }, [selectedItem]);
  
  return (
    <section id="gallery" className="section gallery">
      <div className="container">
        <div className="gallery-title">
          <h2 className={`gallery-heading ${language === 'ml' ? 'malayalam-text' : ''}`}>
            {language === 'en' ? 'Gallery' : 'ഗാലറി'}
          </h2>
          <p className="gallery-subtitle">
            {language === 'en' 
              ? 'Explore our sustainable agriculture practices and farming techniques' 
              : 'ഞങ്ങളുടെ സുസ്ഥിര കൃഷി രീതികളും കൃഷി സാങ്കേതിക വിദ്യകളും പര്യവേക്ഷണം ചെയ്യുക'
            }
          </p>
        </div>
        
        {sets.length > 0 ? (
          // One card per group: use the first ordered item as thumbnail
          <div className="gallery-grid">
            {sets.map((group, gi) => {
              const sorted = (group.images || []).slice().sort((a, b) => (a?.order || 0) - (b?.order || 0));
              const first = sorted[0];
              if (!first) return null;
              const type = first.type || (first.youtubeUrl ? 'youtube' : (first.videoUrl ? 'video' : 'image'));
              const isVideo = type === 'video';
              const isYouTube = type === 'youtube';
              const thumb = isYouTube && first.youtubeUrl ? getYouTubeThumbnail(first.youtubeUrl) : first.imageUrl;
              const caption = group.title || first.caption || (language === 'en' ? 'Gallery Set' : 'ഗാലറി സെറ്റ്');
              const description = first.description || '';
              const items = sorted.map((g) => {
                const t = g.type || (g.youtubeUrl ? 'youtube' : (g.videoUrl ? 'video' : 'image'));
                const cap = g.caption || '';
                const desc = g.description || '';
                return { type: t, src: g.imageUrl, videoUrl: g.videoUrl, youtubeUrl: g.youtubeUrl, caption: { [language]: cap }, description: { [language]: desc } };
              });
              return (
                <div key={group.id || gi} className="gallery-item" onClick={() => openModal(items, 0)}>
                  {isYouTube && thumb ? (
                    <img src={thumb} alt={caption} loading="lazy" onError={(e) => { if (thumb.includes('maxresdefault')) e.currentTarget.src = thumb.replace('maxresdefault','hqdefault'); }} />
                  ) : isVideo && first.videoUrl ? (
                    <video className="gallery-thumb-video" src={first.videoUrl} preload="metadata" muted playsInline />
                  ) : (
                    <img src={thumb} alt={caption} loading="lazy" />
                  )}
                  {(isVideo || isYouTube) && (
                    <div className="play-icon">
                      <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                        <circle cx="30" cy="30" r="30" fill="rgba(255,255,255,0.9)" />
                        <path d="M24 18L42 30L24 42V18Z" fill="#2d5016" />
                      </svg>
                    </div>
                  )}
                  <div className="gallery-overlay">
                    <div className="gallery-caption">{caption}</div>
                    <div className="gallery-description">
                      {description?.split('\n').map((line, i) => (
                        <p key={i} className="description-line">{line}</p>
                      ))}
                    </div>
                    {(group.images?.length || 0) > 1 && (
                      <div style={{ position: 'absolute', right: 8, bottom: 8, background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>
                        {(group.images?.length || 0)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="gallery-grid">
          {imagesToShow.map((item, index) => {
            const isVideo = item.type === 'video' || !!item.videoUrl;
            const isYouTube = item.type === 'youtube' || !!item.youtubeUrl;
            const thumbnail = isYouTube && item.youtubeUrl ? getYouTubeThumbnail(item.youtubeUrl) : item.src;

            return (
              <div key={index} className="gallery-item" onClick={() => openModal(imagesToShow, index)}>
                {isYouTube && thumbnail ? (
                  <img 
                    src={thumbnail} 
                    alt={item.caption[language]} 
                    loading="lazy"
                    onError={(e) => {
                      if (thumbnail.includes('maxresdefault')) {
                        e.currentTarget.src = thumbnail.replace('maxresdefault', 'hqdefault');
                      }
                    }}
                  />
                ) : isVideo && item.videoUrl ? (
                  <video className="gallery-thumb-video" src={item.videoUrl} preload="metadata" muted playsInline />
                ) : (
                  <img src={thumbnail} alt={item.caption[language]} loading="lazy" />
                )}
                {(isVideo || isYouTube) && (
                  <div className="play-icon">
                    <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                      <circle cx="30" cy="30" r="30" fill="rgba(255,255,255,0.9)" />
                      <path d="M24 18L42 30L24 42V18Z" fill="#2d5016" />
                    </svg>
                  </div>
                )}
                <div className="gallery-overlay">
                  <div className="gallery-caption">
                    {item.caption[language]}
                  </div>
                  <div className="gallery-description">
                    {item.description[language]?.split('\n').map((line, lineIndex) => (
                      <p key={lineIndex} className="description-line">{line}</p>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        )}

        {/* If there are sets and also ungrouped singles, render singles grid below */}
        {sets.length > 0 && remoteImages.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div className="gallery-grid">
              {remoteImages.map((item, index) => {
                const isVideo = item.type === 'video' || !!item.videoUrl;
                const isYouTube = item.type === 'youtube' || !!item.youtubeUrl;
                const thumbnail = isYouTube && item.youtubeUrl ? getYouTubeThumbnail(item.youtubeUrl) : item.src;
                return (
                  <div key={`single-${index}`} className="gallery-item" onClick={() => openModal(remoteImages, index)}>
                    {isYouTube && thumbnail ? (
                      <img src={thumbnail} alt={item.caption[language]} loading="lazy" onError={(e) => { if (thumbnail.includes('maxresdefault')) e.currentTarget.src = thumbnail.replace('maxresdefault','hqdefault'); }} />
                    ) : isVideo && item.videoUrl ? (
                      <video className="gallery-thumb-video" src={item.videoUrl} preload="metadata" muted playsInline />
                    ) : (
                      <img src={thumbnail} alt={item.caption[language]} loading="lazy" />
                    )}
                    {(isVideo || isYouTube) && (
                      <div className="play-icon">
                        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                          <circle cx="30" cy="30" r="30" fill="rgba(255,255,255,0.9)" />
                          <path d="M24 18L42 30L24 42V18Z" fill="#2d5016" />
                        </svg>
                      </div>
                    )}
                    <div className="gallery-overlay">
                      <div className="gallery-caption">{item.caption[language]}</div>
                      <div className="gallery-description">
                        {item.description[language]?.split('\n').map((line, lineIndex) => (
                          <p key={lineIndex} className="description-line">{line}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {sets.length === 0 && imagesToShow.length === 0 && (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem 0' }}>
            {language === 'en' ? 'No gallery items found.' : 'ഗാലറി ഇനങ്ങൾ ലഭ്യമല്ല.'}
          </div>
        )}

        {/* View full gallery button if more than 10 items */}
        {(() => {
          const total = sets.length > 0
            ? sets.reduce((n, g) => n + ((g.images || []).length || 0), 0)
            : imagesToShow.length;
          if (total > 10) {
            return (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <a href="#/gallery" className="btn btn-primary">
                  {language === 'en' ? 'View Full Gallery' : 'പൂർണ്ണ ഗാലറി കാണുക'}
                </a>
              </div>
            );
          }
          return null;
        })()}
        
        {/* Modal Popup */}
        {selectedItem && (
          <div className="gallery-modal" onClick={closeModal}>
            <div className="gallery-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="gallery-modal-close" onClick={closeModal}>&times;</button>

              <div className="gallery-modal-row">
                <button className="gallery-nav-btn" onClick={prevItem} aria-label="Previous">&#10094;</button>
                <div className="gallery-modal-media" style={{ flex: 1 }}>
                  {selectedItem.type === 'youtube' || selectedItem.youtubeUrl ? (
                    <iframe
                      src={getYouTubeEmbedUrl(selectedItem.youtubeUrl)}
                      title={selectedItem.caption[language]}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : selectedItem.type === 'video' || selectedItem.videoUrl ? (
                    <video controls autoPlay>
                      <source src={selectedItem.videoUrl || selectedItem.src} />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <img src={selectedItem.src} alt={selectedItem.caption[language]} />
                  )}
                </div>
                <button className="gallery-nav-btn" onClick={nextItem} aria-label="Next">&#10095;</button>
              </div>

              <div className="gallery-modal-details">
                <h3 className="gallery-modal-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{selectedItem.caption[language]}</span>
                  {modalItems.length > 0 && <span style={{ fontSize: 14, color: '#666' }}>{currentIndex + 1} / {modalItems.length}</span>}
                </h3>
                <div className="gallery-modal-description">
                  {selectedItem.description[language]?.split('\n').map((line, lineIndex) => (
                    <p key={lineIndex}>{line}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;
